"use client";

import Image from 'next/image';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { GlassContainer } from '@/components/misc/glass-container';
import { cn, containerClasses } from '@/lib/utils';
import { DebugEditButton } from '@/components/mcp/debug/DebugEditButton';


const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.8 }
  }
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 0.3,
    transition: { duration: 1, delay: 0.5 }
  }
};

export function Hero() {
  const content = {
    title: "Recruitment Reimagined.",
    subtitle: "Human-First, powered by AI.",
    description: "umynd verändert die Art und Weise, wie Menschen und Organisationen sich verbinden.",
    ctaText: "Get Early Access",
    ctaLink: "/contact",
    backgroundImage: "/hero-startseite.png",
    decorativeImage: "/logos/logo-black-print.svg"
  };

  const {
    title,
    subtitle,
    description,
    ctaText,
    ctaLink,
    backgroundImage,
    decorativeImage
  } = content;

  return (
    <section 
      className={cn(
        "relative h-[900px] overflow-hidden",
        "bg-surface"
      )}
      aria-label="Hero section"
    >
      {/* Background Image */}
      <div className="absolute inset-0 group">
        <Image
          src={backgroundImage}
          alt="Decorative background image"
          fill
          priority
          className="object-cover object-center"
          sizes="100vw"
          quality={90}
          aria-hidden="true"
        />
        
        {/* Debug edit button - only shown in debug mode and on hover */}
        <DebugEditButton 
          imageName={backgroundImage.split('/').pop()?.split('.')[0] || ''}
          imageUrl={backgroundImage}
        />
      </div>

      {/* Decorative Fingerprint */}
      <motion.div 
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className={cn(
          "absolute right-[-10%] top-1/2 -translate-y-1/2",
          "w-[800px] h-[800px]",
          "hidden md:block"
        )}
      >
        <div className="relative w-full h-full group">
          <Image
            src={decorativeImage}
            alt=""
            fill
            className={cn(
              "object-contain filter",
              "-ml-20 -mt-20",
              "brightness-0 invert opacity-30"
            )}
            sizes="(max-width: 768px) 0, (max-width: 1200px) 600px, 800px"
            aria-hidden="true"
          />
          
          {/* Debug edit button - only shown in debug mode and on hover */}
          <DebugEditButton 
            imageName={decorativeImage.split('/').pop()?.split('.')[0] || ''}
            imageUrl={decorativeImage}
          />
        </div>
      </motion.div>

      {/* Content Container */}
      <div className={cn(containerClasses, "h-full")}>
        <div className="relative z-10 h-full flex flex-col justify-center">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="relative max-w-[660px]"
          >
            <GlassContainer intensity="heavy">
              <h1 className={cn(
                "text-h2 mb-6",
                "whitespace-pre-line"
              )}>
                {title}
                <br />
                {subtitle.split('\n').map((part, index, array) => (
                  <span key={index}>
                    {part}
                    {index < array.length - 1 && <br />}
                  </span>
                ))}
              </h1>

              <div className="text-body-lg">
                <div className={cn(
                  "mb-6",
                  "text-muted-foreground"
                )}
                  aria-label="Hero description"
                >
                  {description}
                </div>
              </div>
            </GlassContainer>

            {/* Call to Action Buttons */}
            <motion.div 
              variants={fadeInUp}
              className={cn(
                "relative mt-8 sm:mt-10 z-20",
                "flex flex-col gap-3 sm:gap-4",
                "max-w-[330px]"
              )}
            >
              <Link href={ctaLink} passHref>
                <Button 
                  variant="light-primary"
                  size="lg"
                  className={cn(
                    "w-full"
                  )}
                >
                  {ctaText}
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
} 